// NameOriginCalculator.jsx
import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const NameOriginCalculator = () => {
  const [name, setName] = useState('');
  const [origin, setOrigin] = useState('');

  const calculateOrigin = (e) => {
    e.preventDefault(); // Prevent the default form submission behavior

    // Mock origin calculation logic
    if (name) {
      setOrigin(`The origin of the name "${name}" is mysterious and ancient.`);
    } else {
      setOrigin('');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <Helmet>
        <title>Name Origin Calculator | Discover Your Name's History</title>
        <meta name="description" content="Discover the historical and cultural origins of names with our Name Origin Calculator. Explore the roots and significance of any name." />
        <meta name="keywords" content="name origin calculator, name history, name etymology, cultural names, historical names" />
        <meta property="og:title" content="Name Origin Calculator | Discover Your Name's History" />
        <meta property="og:description" content="Discover the historical and cultural origins of names with our Name Origin Calculator. Explore the roots and significance of any name." />
        <meta property="og:image" content="https://yourdomain.com/path/to/image.jpg" />
        <meta property="og:url" content="https://yourdomain.com/name-origin-calculator" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div className="container mx-auto px-4 max-w-7xl">
        <header className="mb-8 text-center">
          <div className="text-4xl font-bold text-purple-600 mb-2">🌟</div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Name Origin Calculator</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Discover the origin of any name with our Name Origin Calculator.
          </p>
        </header>

        <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
          <form className="space-y-6" onSubmit={calculateOrigin}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Enter a name"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-purple-700 transition duration-200"
            >
              Calculate Origin
            </button>

            {origin && (
              <div className="mt-6">
                <p className="text-lg font-semibold">{origin}</p>
              </div>
            )}
          </form>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Enter a name in the input field.</li>
            <li>Click the 'Calculate Origin' button.</li>
            <li>View the calculated origin below the button.</li>
          </ol>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Features & Benefits</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Instant Results</h3>
              <p className="text-gray-700">
                Get immediate insights into the origin of any name.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800">User-Friendly Interface</h3>
              <p className="text-gray-700">
                Easy-to-use interface with clear instructions and intuitive design.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Educational</h3>
              <p className="text-gray-700">
                Learn about the historical and cultural significance of names.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQs</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">What is the Name Origin Calculator?</h3>
              <p className="text-gray-700">
                The Name Origin Calculator is a tool that helps you discover the origin of a given name.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800">How accurate is the calculation?</h3>
              <p className="text-gray-700">
                The calculation is based on a mock logic and is for demonstration purposes only.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-800">Can I use this tool for any name?</h3>
              <p className="text-gray-700">
                Yes, you can use this tool for any name, but the results are for illustrative purposes.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NameOriginCalculator;